/**
 * Announcement API Service
 *
 * 공지사항 관리 API 통신 서비스
 *
 * 【설명】
 * - 공지사항 CRUD 작업을 위한 API 호출 메서드 제공
 * - 검색, 첨부파일, 조회수 기능 지원
 * - Dependency Injection 지원으로 baseUrl, endpoints 커스터마이징 가능
 *
 * 【사용법】
 * ```javascript
 * import { defaultAnnouncementAPI } from './announcementApi';
 *
 * // 활성 공지사항 조회 (사용자용)
 * const announcements = await defaultAnnouncementAPI.getActiveAnnouncements();
 *
 * // 전체 공지사항 조회 (관리자용) - totalCount 포함
 * const { items, totalCount } = await defaultAnnouncementAPI.getAllAnnouncements();
 *
 * // 검색 조건으로 조회
 * const { items, totalCount } = await defaultAnnouncementAPI.searchAnnouncements({
 *   type: 'info',
 *   isActive: true,
 *   keyword: '시스템'
 * });
 *
 * // 공지사항 생성
 * const newAnnouncement = await defaultAnnouncementAPI.createAnnouncement({
 *   title: '시스템 점검 안내',
 *   content: '...',
 *   type: 'important',
 *   authorId: 'admin',
 *   authorName: '관리자'
 * });
 *
 * // 첨부파일 업로드
 * const attachment = await defaultAnnouncementAPI.uploadAttachment(announcementId, file);
 * ```
 */

import { API_BASE_URL } from '../../constants/api';
import { authFetch } from './apiClient';

/**
 * Announcement API 클래스
 */
export class AnnouncementAPI {
  /**
   * AnnouncementAPI 생성자
   *
   * @param {Object} config - 설정 객체
   * @param {string} config.baseUrl - API 베이스 URL (기본: API_BASE_URL)
   * @param {Object} config.endpoints - 엔드포인트 경로
   * @param {Object} config.logger - 로거 객체 (기본: console)
   */
  constructor({
    baseUrl = API_BASE_URL,
    endpoints = {
      announcements: '/api/content/announcement',
    },
    logger = console,
  } = {}) {
    this.baseUrl = baseUrl;
    this.endpoints = endpoints;
    this.logger = logger;
  }

  /**
   * Authorization 헤더 생성
   * @private
   * @returns {Object} 헤더 객체
   */
  _getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
    };
  }

  /**
   * 활성 공지사항 조회 (사용자용)
   * - isActive가 true이고 현재 날짜가 startDate~endDate 범위 내인 공지사항만 반환
   *
   * @returns {Promise<AnnouncementDTO[]>}
   */
  async getActiveAnnouncements() {
    try {
      const response = await authFetch(`${this.baseUrl}${this.endpoints.announcements}/search`, {
        method: 'POST',
        headers: this._getAuthHeaders(),
        body: JSON.stringify({ isActive: true, pageSize: 1000 }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const page = await response.json();
      return page.content || [];
    } catch (error) {
      this.logger.error('Failed to fetch active announcements:', error);
      throw new Error('공지사항을 불러오는데 실패했습니다.');
    }
  }

  /**
   * 전체 공지사항 조회 (관리자용)
   *
   * @returns {Promise<AnnouncementListResponse>} { items, totalCount, count }
   */
  async getAllAnnouncements() {
    try {
      const response = await authFetch(`${this.baseUrl}${this.endpoints.announcements}/search`, {
        method: 'POST',
        headers: this._getAuthHeaders(),
        body: JSON.stringify({
          pageSize: 1000,
          sortColumn: 'createdAt',
          sortAscending: false, // 최신순 (내림차순)
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const page = await response.json();
      return {
        items: page.content || [],
        totalCount: page.totalElements || 0,
        count: page.numberOfElements || 0,
      };
    } catch (error) {
      this.logger.error('Failed to fetch all announcements:', error);
      throw new Error('공지사항 목록을 불러오는데 실패했습니다.');
    }
  }

  /**
   * 검색 조건으로 공지사항 조회
   *
   * @param {AnnouncementSearchParams} params - 검색 조건
   * @returns {Promise<AnnouncementListResponse>} { items, totalCount, count }
   */
  /**
   * 공지사항 페이지네이션 검색 (서버 사이드 페이지네이션)
   *
   * @param {Object} params - 검색 파라미터 (AnnouncementSearchParams + pageNumber + pageSize)
   * @returns {Promise<{content: AnnouncementDTO[], totalElements: number, totalPages: number, number: number, size: number}>}
   */
  async searchAnnouncementsPaginated(params = {}) {
    try {
      const { pageNumber = 0, pageSize = 20, ...rest } = params;
      const searchBody = { pageNumber, pageSize };

      if (rest.category && rest.category !== 'all') searchBody.category = rest.category;
      if (rest.isActive !== undefined && rest.isActive !== null)
        searchBody.isActive = rest.isActive;
      if (rest.isPinned !== undefined && rest.isPinned !== null)
        searchBody.isPinned = rest.isPinned;
      if (rest.keyword) searchBody.title = `%${rest.keyword}%`;
      if (rest.titleKeyword) searchBody.title = `%${rest.titleKeyword}%`;
      if (rest.createdBy) searchBody.createdBy = rest.createdBy;
      if (rest.createdFrom) searchBody.createdAtFrom = rest.createdFrom + ' 00:00:00';
      if (rest.createdTo) searchBody.createdAtTo = rest.createdTo + ' 23:59:59';
      if (rest.sortBy) searchBody.sortColumn = rest.sortBy;
      if (rest.sortDirection) searchBody.sortAscending = rest.sortDirection === 'asc';

      const response = await authFetch(`${this.baseUrl}${this.endpoints.announcements}/search`, {
        method: 'POST',
        headers: this._getAuthHeaders(),
        body: JSON.stringify(searchBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to search announcements paginated:', error);
      throw new Error('공지사항 검색에 실패했습니다.');
    }
  }

  async searchAnnouncements(params = {}) {
    try {
      const searchBody = { pageSize: 1000 };

      if (params.category && params.category !== 'all') searchBody.category = params.category;
      if (params.isActive !== undefined && params.isActive !== null)
        searchBody.isActive = params.isActive;
      if (params.isPinned !== undefined && params.isPinned !== null)
        searchBody.isPinned = params.isPinned;
      if (params.keyword) searchBody.title = `%${params.keyword}%`;
      if (params.titleKeyword) searchBody.title = `%${params.titleKeyword}%`;
      if (params.createdBy) searchBody.createdBy = params.createdBy;
      if (params.createdFrom) searchBody.createdAtFrom = params.createdFrom + ' 00:00:00';
      if (params.createdTo) searchBody.createdAtTo = params.createdTo + ' 23:59:59';
      if (params.sortBy) searchBody.sortColumn = params.sortBy;
      if (params.sortDirection) searchBody.sortAscending = params.sortDirection === 'asc';

      const response = await authFetch(`${this.baseUrl}${this.endpoints.announcements}/search`, {
        method: 'POST',
        headers: this._getAuthHeaders(),
        body: JSON.stringify(searchBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const page = await response.json();
      return {
        items: page.content || [],
        totalCount: page.totalElements || 0,
        count: page.numberOfElements || 0,
      };
    } catch (error) {
      this.logger.error('Failed to search announcements:', error);
      throw new Error('공지사항 검색에 실패했습니다.');
    }
  }

  /**
   * 공지사항 전체 수 조회
   *
   * @returns {Promise<{ totalCount: number }>}
   */
  async getTotalCount() {
    try {
      const response = await authFetch(`${this.baseUrl}${this.endpoints.announcements}/search`, {
        method: 'POST',
        headers: this._getAuthHeaders(),
        body: JSON.stringify({ pageSize: 1 }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const page = await response.json();
      return { totalCount: page.totalElements || 0 };
    } catch (error) {
      this.logger.error('Failed to fetch announcement count:', error);
      throw new Error('공지사항 수를 불러오는데 실패했습니다.');
    }
  }

  /**
   * 조회수 증가
   *
   * @param {string} id - 공지사항 ID
   * @returns {Promise<void>}
   */
  async incrementViewCount(id) {
    try {
      const response = await authFetch(
        `${this.baseUrl}${this.endpoints.announcements}/${id}/view`,
        {
          method: 'POST',
          headers: this._getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      this.logger.error('Failed to increment view count:', error);
      // 조회수 증가 실패는 사용자에게 표시하지 않음
    }
  }

  /**
   * 공지사항 상세 조회
   *
   * @param {string} id - 공지사항 ID
   * @returns {Promise<AnnouncementDTO>}
   */
  async getAnnouncementById(id) {
    try {
      const response = await authFetch(`${this.baseUrl}${this.endpoints.announcements}/${id}`, {
        headers: this._getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to fetch announcement:', error);
      throw new Error('공지사항을 불러오는데 실패했습니다.');
    }
  }

  /**
   * 공지사항 생성
   *
   * @param {CreateAnnouncementRequest} data - 생성할 공지사항 데이터
   * @returns {Promise<AnnouncementDTO>}
   */
  async createAnnouncement(data) {
    try {
      const response = await authFetch(`${this.baseUrl}${this.endpoints.announcements}`, {
        method: 'POST',
        headers: this._getAuthHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to create announcement:', error);
      throw new Error('공지사항 생성에 실패했습니다: ' + error.message);
    }
  }

  /**
   * 공지사항 수정
   *
   * @param {string} id - 공지사항 ID
   * @param {UpdateAnnouncementRequest} data - 수정할 데이터
   * @returns {Promise<AnnouncementDTO>}
   */
  async updateAnnouncement(id, data) {
    try {
      const response = await authFetch(`${this.baseUrl}${this.endpoints.announcements}`, {
        method: 'PUT',
        headers: this._getAuthHeaders(),
        body: JSON.stringify({ ...data, id }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to update announcement:', error);
      throw new Error('공지사항 수정에 실패했습니다: ' + error.message);
    }
  }

  /**
   * 공지사항 삭제
   *
   * @param {string} id - 공지사항 ID
   * @returns {Promise<{ success: boolean }>}
   */
  async deleteAnnouncement(id) {
    try {
      const response = await authFetch(`${this.baseUrl}${this.endpoints.announcements}/${id}`, {
        method: 'DELETE',
        headers: this._getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to delete announcement:', error);
      throw new Error('공지사항 삭제에 실패했습니다: ' + error.message);
    }
  }

  // ===== 첨부파일 관련 메서드 =====

  /**
   * 첨부파일 업로드
   *
   * @param {string} announcementId - 공지사항 ID
   * @param {File} file - 업로드할 파일
   * @returns {Promise<AttachmentDTO>}
   */
  async uploadAttachment(announcementId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authFetch(
        `${this.baseUrl}${this.endpoints.announcements}/${announcementId}/attachments`,
        {
          method: 'POST',
          headers: {},
          body: formData,
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to upload attachment:', error);
      throw new Error('첨부파일 업로드에 실패했습니다: ' + error.message);
    }
  }

  /**
   * 첨부파일 다운로드 URL 생성
   *
   * @param {string} announcementId - 공지사항 ID
   * @param {string} attachmentId - 첨부파일 ID
   * @returns {string} 다운로드 URL
   */
  getAttachmentDownloadUrl(announcementId, attachmentId) {
    return `${this.baseUrl}${this.endpoints.announcements}/${announcementId}/attachments/${attachmentId}/download`;
  }

  /**
   * 첨부파일 다운로드
   *
   * @param {string} announcementId - 공지사항 ID
   * @param {string} attachmentId - 첨부파일 ID
   * @param {string} fileName - 저장할 파일명
   */
  async downloadAttachment(announcementId, attachmentId, fileName) {
    try {
      const response = await authFetch(
        `${this.baseUrl}${this.endpoints.announcements}/${announcementId}/attachments/${attachmentId}/download`,
        {
          headers: {},
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      this.logger.error('Failed to download attachment:', error);
      throw new Error('첨부파일 다운로드에 실패했습니다.');
    }
  }

  /**
   * 첨부파일 삭제
   *
   * @param {string} announcementId - 공지사항 ID
   * @param {string} attachmentId - 첨부파일 ID
   * @returns {Promise<{ success: boolean }>}
   */
  async deleteAttachment(announcementId, attachmentId) {
    try {
      const response = await authFetch(
        `${this.baseUrl}${this.endpoints.announcements}/${announcementId}/attachments/${attachmentId}`,
        {
          method: 'DELETE',
          headers: this._getAuthHeaders(),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Failed to delete attachment:', error);
      throw new Error('첨부파일 삭제에 실패했습니다: ' + error.message);
    }
  }
}

/**
 * 기본 AnnouncementAPI 인스턴스
 */
export const defaultAnnouncementAPI = new AnnouncementAPI();

/**
 * TypeScript-style type definitions (JSDoc)
 *
 * @typedef {Object} AnnouncementDTO
 * @property {string} id - 공지사항 ID
 * @property {number} sequenceNumber - 글 번호
 * @property {string} title - 제목
 * @property {string} content - 내용
 * @property {string} type - 유형 ('info' | 'warning' | 'important' | 'feature' | 'update' | 'notice' | 'maintenance')
 * @property {string} [authorId] - 작성자 ID
 * @property {string} [authorName] - 작성자 이름
 * @property {boolean} isActive - 활성 상태
 * @property {boolean} isPinned - 상단 고정 여부
 * @property {number} viewCount - 조회수
 * @property {string} [startDate] - 게시 시작일
 * @property {string} [endDate] - 게시 종료일
 * @property {string} createdAt - 생성일시
 * @property {string} updatedAt - 수정일시
 * @property {AttachmentDTO[]} [attachments] - 첨부파일 목록
 *
 * @typedef {Object} AttachmentDTO
 * @property {string} id - 첨부파일 ID
 * @property {string} originalFileName - 원본 파일명
 * @property {string} storedFileName - 저장된 파일명
 * @property {string} contentType - MIME 타입
 * @property {number} fileSize - 파일 크기 (bytes)
 * @property {string} createdAt - 업로드 일시
 *
 * @typedef {Object} AnnouncementListResponse
 * @property {AnnouncementDTO[]} items - 공지사항 목록
 * @property {number} totalCount - 전체 공지사항 수
 * @property {number} count - 현재 목록 수
 *
 * @typedef {Object} AnnouncementSearchParams
 * @property {string} [type] - 유형 필터
 * @property {boolean} [isActive] - 활성 상태 필터
 * @property {boolean} [isPinned] - 고정 여부 필터
 * @property {string} [titleKeyword] - 제목 검색어
 * @property {string} [contentKeyword] - 내용 검색어
 * @property {string} [keyword] - 제목+내용 통합 검색어
 * @property {string} [authorName] - 작성자명 검색
 * @property {string} [createdFrom] - 작성일 시작 (YYYY-MM-DD)
 * @property {string} [createdTo] - 작성일 종료 (YYYY-MM-DD)
 * @property {string} [sortBy] - 정렬 기준 ('sequenceNumber' | 'createdAt' | 'title' | 'type' | 'authorName')
 * @property {string} [sortDirection] - 정렬 방향 ('asc' | 'desc')
 *
 * @typedef {Object} CreateAnnouncementRequest
 * @property {string} title - 제목 (필수, 최대 200자)
 * @property {string} content - 내용 (필수)
 * @property {string} [type='info'] - 유형
 * @property {string} [authorId] - 작성자 ID
 * @property {string} [authorName] - 작성자 이름
 * @property {boolean} [isActive=true] - 활성 상태
 * @property {boolean} [isPinned=false] - 상단 고정
 * @property {string} [startDate] - 게시 시작일 (ISO 8601)
 * @property {string} [endDate] - 게시 종료일 (ISO 8601)
 *
 * @typedef {Object} UpdateAnnouncementRequest
 * @property {string} [title] - 제목
 * @property {string} [content] - 내용
 * @property {string} [type] - 유형
 * @property {boolean} [isActive] - 활성 상태
 * @property {boolean} [isPinned] - 상단 고정
 * @property {string} [startDate] - 게시 시작일
 * @property {string} [endDate] - 게시 종료일
 */
